import React, { useState } from 'react';
    import { Link, useNavigate, useLocation } from 'react-router-dom';
    import { Input } from '@/components/ui/Input';
    import { Button } from '@/components/ui/Button';
    import { useAuth } from '@/contexts/AuthContext';
    import { FiAlertCircle, FiShield, FiMail, FiRefreshCw } from 'react-icons/fi';
    import supabase from '@/supabase/supabase';
    import toast from 'react-hot-toast';

    export function LoginPage() {
      const [email, setEmail] = useState('');
      const [password, setPassword] = useState('');
      const [error, setError] = useState('');
      const [isLoading, setIsLoading] = useState(false);
      const [isResending, setIsResending] = useState(false);
      const [pendingVerification, setPendingVerification] = useState(false);
      
      const { signIn } = useAuth();
      const navigate = useNavigate();
      const location = useLocation();
      
      const from = location.state?.from?.pathname || '/dashboard';

      const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
          const { data, error: signInError } = await signIn(email, password);
          
          if (signInError) {
            if (signInError.message?.toLowerCase().includes('email not confirmed')) {
              setPendingVerification(true);
            } else {
              setError(signInError.message);
            }
            return;
          }

          if (data?.user) {
            toast.success('Welcome back!');
            navigate(from, { replace: true });
          }
        } catch (err) {
          setError('An unexpected error occurred during sign in.');
        } finally {
          setIsLoading(false);
        }
      };

      const handleResend = async () => {
        setIsResending(true);
        try {
          const { error: resendError } = await supabase.auth.resend({
            type: 'signup',
            email,
            options: {
              emailRedirectTo: `${window.location.origin}/login`
            }
          });
          if (resendError) throw resendError;
          toast.success('Verification link sent!');
        } catch (err) {
          toast.error(err.message || 'Failed to resend link');
        } finally {
          setIsResending(false);
        }
      };

      if (pendingVerification) {
        return (
          <div className="space-y-6 text-center">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiMail className="w-8 h-8 text-blue-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Verify Your Email</h1>
            <p className="text-slate-500 mt-2 text-sm leading-relaxed">
              Your email <strong>{email}</strong> is not yet verified.
            </p>
            <div className="space-y-3">
              <Button className="w-full h-12 font-bold" onClick={() => window.location.reload()}>
                <FiRefreshCw className="mr-2" /> Check Status
              </Button>
              <button 
                onClick={handleResend}
                disabled={isResending}
                className="text-xs font-black text-primary-500 uppercase tracking-widest disabled:opacity-50"
              >
                {isResending ? 'Sending...' : 'Resend Link'}
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-tight">Welcome back</h1>
          
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3 text-red-600 text-sm">
              <FiAlertCircle className="shrink-0 mt-0.5" />
              <p className="font-bold">{error}</p>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <Input 
              label="Email Address" 
              type="email" 
              name="email"
              autoComplete="email"
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              required 
            />
            
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Password</label>
                <Link to="/forgot-password" size="sm" className="text-xs font-bold text-primary-500 hover:text-primary-600">
                  Forgot?
                </Link>
              </div>
              <Input 
                type="password" 
                name="password"
                autoComplete="current-password"
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                required 
              />
            </div>

            <Button type="submit" className="w-full h-12 rounded-xl text-sm font-black uppercase tracking-widest" isLoading={isLoading}>
              Sign in
            </Button>
          </form>

          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
            <div className="relative flex justify-center text-[10px] uppercase font-black tracking-widest">
              <span className="bg-slate-50 px-3 text-slate-400">Quick Access</span>
            </div>
          </div>

          <Button 
            type="button" 
            variant="outline" 
            className="w-full border-2 border-dashed rounded-xl h-11 text-xs font-bold"
            onClick={() => {
              setEmail('admin@admin.com');
              setPassword('123123');
            }}
          >
            <FiShield className="mr-2 text-primary-500" /> Admin Demo
          </Button>

          <p className="text-center text-sm text-slate-500">
            New to Roaming Logos?{' '}
            <Link to="/register" className="font-bold text-primary-600 hover:underline">Sign up</Link>
          </p>
        </div>
      );
    }